import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";

const TermsOfServicePage = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <a href="/" className="text-2xl font-heading font-bold text-[#e6007e]">Paify</a>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-heading font-bold text-gray-900 mb-8">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: January {currentYear}</p>
        
        <div className="prose prose-gray max-w-none space-y-8">
          {/* Agreement to Terms */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              By accessing or using Paify ("the Platform"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Platform.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              These Terms apply to all users of the Platform, including business owners ("Service Providers") who create contracts and receive payments, and their clients ("Clients") who make payments through the Platform.
            </p>
          </section>
          
          {/* Description of Service */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              Paify is a payment management platform that enables Service Providers to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2 mt-2">
              <li>Create and manage projects with defined payment structures</li>
              <li>Generate digital contracts for electronic signature</li>
              <li>Accept payments from clients via various payment methods (card, Apple Pay, Google Pay, bank transfer)</li>
              <li>Track payment milestones and manage invoicing</li>
            </ul>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Paify acts as an intermediary platform and payment facilitator. We do not directly provide the services described in contracts between Service Providers and their Clients.
            </p>
          </section>
          
          {/* Account Registration */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">3. Account Registration</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              To use certain features of the Platform, you must register for an account. When you register, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2 mt-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorised use of your account</li>
            </ul>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              You must be at least 18 years old and capable of forming a binding contract to use this Platform.
            </p>
          </section>
          
          {/* Subscription Plans and Fees */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">4. Subscription Plans and Fees</h2>
            <p className="text-gray-600 font-body leading-relaxed mb-4">
              Paify offers various subscription plans with different features and pricing:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2">
              <li><strong>Standard Plan:</strong> Basic features with a 3% platform fee on transactions</li>
              <li><strong>Paify Plus:</strong> Enhanced features with a 2% platform fee on transactions</li>
              <li><strong>Embedded:</strong> White-label solution with a 2% platform fee on transactions</li>
            </ul>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Subscription fees are billed in advance on a monthly or annual basis. Platform fees are deducted automatically from each transaction processed through the Platform.
            </p>
          </section>
          
          {/* Payment Processing */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">5. Payment Processing</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              Payment processing services are provided by Stripe and are subject to the Stripe Connected Account Agreement, which includes the Stripe Terms of Service. By using Paify, you agree to be bound by Stripe's terms.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Service Providers must connect their Stripe account to receive payments. Paify is not responsible for any issues arising from Stripe's services, including but not limited to payment failures, delays, or disputes.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Funds from client payments will be transferred to Service Providers' connected Stripe accounts, minus applicable platform fees, according to Stripe's standard payout schedule.
            </p>
          </section>
          
          {/* Electronic Signatures */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">6. Electronic Signatures and Contracts</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              The Platform enables electronic signature of contracts between Service Providers and Clients. By using the electronic signature feature, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2 mt-2">
              <li>Electronic signatures are legally binding under the Electronic Communications Act 2000 and eIDAS Regulation</li>
              <li>You consent to conducting transactions electronically</li>
              <li>Contracts signed electronically have the same legal effect as handwritten signatures</li>
              <li>You are responsible for the accuracy of information in contracts you create or sign</li>
            </ul>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Paify is not a party to contracts between Service Providers and Clients and is not responsible for the content, validity, or enforcement of such contracts.
            </p>
          </section>
          
          {/* User Responsibilities */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">7. User Responsibilities</h2>
            <p className="text-gray-600 font-body leading-relaxed mb-4">
              When using the Platform, you agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 font-body space-y-2">
              <li>Use the Platform for any illegal or unauthorised purpose</li>
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>Submit false, misleading, or fraudulent information</li>
              <li>Attempt to gain unauthorised access to other accounts or systems</li>
              <li>Interfere with or disrupt the Platform's functionality</li>
              <li>Use the Platform to facilitate money laundering or terrorist financing</li>
              <li>Circumvent or manipulate the fee structure</li>
            </ul>
          </section>
          
          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              The Platform and its original content, features, and functionality are owned by Paify and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              You retain ownership of any content you submit to the Platform (such as contract content, business information, and project details). By submitting content, you grant Paify a non-exclusive, worldwide, royalty-free licence to use, store, and display such content solely for the purpose of providing the Platform's services.
            </p>
          </section>
          
          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              To the maximum extent permitted by law, Paify shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising out of or in connection with your use of the Platform.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Our total liability to you for any claims arising from these Terms or your use of the Platform shall not exceed the amount you paid to Paify in the twelve (12) months preceding the claim.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Paify is not responsible for disputes between Service Providers and Clients regarding the quality, delivery, or performance of services described in contracts.
            </p>
          </section>
          
          {/* Disclaimer of Warranties */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">10. Disclaimer of Warranties</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              The Platform is provided on an "as is" and "as available" basis without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              We do not warrant that the Platform will be uninterrupted, secure, or error-free, or that defects will be corrected.
            </p>
          </section>
          
          {/* Termination */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              We may terminate or suspend your account and access to the Platform immediately, without prior notice or liability, for any reason, including but not limited to breach of these Terms.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              You may terminate your account at any time by contacting us. Upon termination, your right to use the Platform will immediately cease. Any outstanding payments or platform fees owed will remain due.
            </p>
          </section>
          
          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              Any disputes arising from these Terms or your use of the Platform shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>
          
          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
            <p className="text-gray-600 font-body leading-relaxed mt-4">
              By continuing to access or use our Platform after revisions become effective, you agree to be bound by the revised terms.
            </p>
          </section>
          
          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-4">14. Contact Information</h2>
            <p className="text-gray-600 font-body leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-100 rounded-lg">
              <p className="text-gray-700 font-body"><strong>Email:</strong> legal@paify.co</p>
              <p className="text-gray-700 font-body mt-2"><strong>Address:</strong> United Kingdom</p>
            </div>
          </section>
        </div>
        
        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex space-x-6">
            <a href="/privacy-policy" className="text-[#e6007e] hover:underline font-body">Privacy Policy</a>
            <a href="/" className="text-[#e6007e] hover:underline font-body">Back to Home</a>
          </div>
        </div>
      </main>
    </div>
  );
};

// Help Page Component


export default TermsOfServicePage;
